import React, { useEffect, useMemo, useState } from 'react';
import { Stack } from '@mui/material';
import Grid from '@mui/material/Grid2';
import CusButton from '../../../components/material-ui/CusButton';
import SelectGroup from '../../../components/casino/SelectGroup';
import dayjs from 'dayjs';
import { getOnlyData } from '../../../utils';
import { useAppSelector } from '../../../store';
import { TEST_MODE } from '../../../enums/enums';
import { useTranslation } from 'react-i18next';
import CurrencySelect from '../../../components/shared/CurrencySelect';
import UserAutoComplete from '../../../components/material-ui/userAutoComplete';
import { API_GAMES } from '../../../api/games';
import { useDispatch } from 'react-redux';
import {
  setPlayers,
  setProviders,
  setSource,
  setStatisticFilter,
  setStatisticResult
} from '../../../store/apps/gamesInfoSlice';
import CusDatePicker from '../../../components/material-ui/CusDatePicker';
import CusLabelSelect from '../../../components/material-ui/CusLabelSelect';
import DatePreset from '../../../components/material-ui/DatePreset';
import useBootstrapSize from '../../../hooks/useBootstrapSize';

export default function StatisticFilter({ setLoader, setEmpty }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const prevWeek = getOnlyData(dayjs().subtract(7, 'day'));
  const today = getOnlyData(dayjs());
  const { isMobile } = useBootstrapSize();

  const mainGroup = useAppSelector((state) => state.user.group.main);
  const groupHash = useAppSelector((state) => state.user.group.hash);
  const source = useAppSelector((state) => state.games.source);
  const providers = useAppSelector((state) => state.games.providers);
  const players = useAppSelector((state) => state.games.players);
  const statisticFilter = useAppSelector((state) => state.games.statistic.filter);

  const mainGroupId = mainGroup.groupId;
  const gridSize = { xs: 12, sm: 6 };

  const [filterInfo, setFilterInfo] = useState({});

  console.log('providers', providers);

  const dateFrom = filterInfo.dateFrom;
  const dateTo = filterInfo.dateTo;
  const groupId = filterInfo.groupId;
  const sourceId = filterInfo.sourceId;

  const groupPlayers = players[groupId] || [];
  const providersSource = providers[sourceId] || {};

  const playersOptions = useMemo(() => {
    const playersOptions = groupPlayers?.map((el) => ({ label: el.login, value: el.id }));
    playersOptions.unshift({ value: null, label: t('All') });

    return playersOptions;
  }, [groupPlayers]);

  const providerOptions = useMemo(() => {
    const idProviders = Object.keys(providersSource);
    const providerOptions = idProviders.map((el) => ({ label: providersSource[el], value: el }));
    providerOptions.unshift({ value: 'any', label: t('All') });

    return providerOptions;
  }, [providersSource]);

  const sourceOptions = useMemo(() => {
    const sourceKeys = Object.keys(source);
    const sourceOptions = sourceKeys.map((id) => ({ label: source[id]?.name, value: id }));
    sourceOptions.unshift({ value: 'any', label: t('All') });

    return sourceOptions;
  }, [source]);

  useEffect(() => {
    if (source.length) return;
    if (TEST_MODE) return;

    API_GAMES.list
      .source()
      .then((e) => {
        const list = e.data.list;
        dispatch(setSource(list));
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    const group = groupHash[groupId];

    if (!groupId) return;
    if (groupPlayers.length) return;
    if (!group.game) return;
    if (TEST_MODE) return;

    API_GAMES.list
      .users({ groupId })
      .then((e) => {
        const list = e.data.list;
        dispatch(setPlayers({ data: list, groupId }));
      })
      .catch(console.error);
  }, [groupId]);

  useEffect(() => {
    if (!sourceId) return;
    if (providersSource.length) return;
    if (sourceId == 'any') return;
    if (TEST_MODE) return;

    API_GAMES.list
      .providers({ sourceId })
      .then((e) => {
        const list = e.data.list;
        dispatch(setProviders({ data: list, sourceId }));
      })
      .catch(console.error);
  }, [sourceId]);

  const onSaveClick = (filterInfo) => {
    const filter = { ...filterInfo, gameMode: 'real' };
    if (!filter.userId) filter.userId = null;
    if (+filter.providerId) filter.providerId = +filter.providerId;
    if (filter.providerId == 'any') filter.providerId = null;

    setLoader(true);

    API_GAMES.report
      .statistic(filter)
      .then((e) => {
        const data = e.data.data;
        dispatch(setStatisticFilter(filter));
        dispatch(setStatisticResult(data));
        setEmpty(!data.length);
      })
      .catch(console.error)
      .finally(() => setLoader(false));
  };

  useEffect(() => {
    setFilterInfo({
      groupId: statisticFilter.groupId || mainGroupId,
      userId: statisticFilter.userId || 0,
      currency: statisticFilter.currency || mainGroup.currency,
      sourceId: statisticFilter.source || 'any',
      dateFrom: statisticFilter.dateFrom || prevWeek,
      dateTo: statisticFilter.dateTo || today,
      providerId: statisticFilter.providerId || 'any'
    });
  }, [mainGroupId]);

  return (
    <Stack gap={2}>
      <Grid container spacing={1}>
        <Grid size={12}>
          <SelectGroup
            title="Group"
            hideTooltip
            selectGroupId={groupId}
            setSelectGroupId={(val) => {
              setFilterInfo((prev) => ({ ...prev, groupId: val, userId: 0 }));
            }}
          />
        </Grid>
        <Grid size={gridSize}>
          <UserAutoComplete
            groupId={groupId}
            options={playersOptions}
            userId={filterInfo.userId}
            setUserId={(value) => setFilterInfo((prev) => ({ ...prev, userId: value }))}
          />
        </Grid>
        <Grid size={gridSize}>
          <CurrencySelect
            value={filterInfo.currency}
            setValue={(val) => setFilterInfo((prev) => ({ ...prev, currency: val }))}
          />
        </Grid>
        <Grid size={gridSize} mt={1}>
          <CusDatePicker
            label="Start Date"
            value={dateFrom}
            onChange={(val) => setFilterInfo((prev) => ({ ...prev, dateFrom: val }))}
            formatChange="YYYY-MM-DD"
          />
        </Grid>
        <Grid size={gridSize} mt={1}>
          <CusDatePicker
            label="End Date"
            value={dateTo}
            onChange={(val) => setFilterInfo((prev) => ({ ...prev, dateTo: val }))}
            formatChange="YYYY-MM-DD"
            errorText={
              dateFrom && dateTo && new Date(dateTo).getTime() < new Date(dateFrom).getTime()
                ? t('End date must be later than start date')
                : ''
            }
          />
        </Grid>
        <Grid size={12} mt={-0.5}>
          <DatePreset
            fontSize={isMobile ? 12 : 14}
            setStartDate={(val) => setFilterInfo((prev) => ({ ...prev, dateFrom: val }))}
            setEndDate={(val) => setFilterInfo((prev) => ({ ...prev, dateTo: val }))}
            onlyDate
          />
        </Grid>
        <Grid size={gridSize}>
          <CusLabelSelect
            label={'Pool'}
            isTranslateOptions={false}
            value={sourceId}
            options={sourceOptions}
            onChange={(val) =>
              setFilterInfo((prev) => ({ ...prev, sourceId: val, providerId: 'any' }))
            }
          />
        </Grid>
        <Grid size={gridSize}>
          <CusLabelSelect
            label={'Provider'}
            value={filterInfo.providerId}
            options={providerOptions}
            onChange={(val) => setFilterInfo((prev) => ({ ...prev, providerId: val }))}
          />
        </Grid>
      </Grid>
      <CusButton
        ml="auto"
        sx={{ px: 5 }}
        text="Show"
        variant="contained"
        onClick={() => onSaveClick(filterInfo)}
      />
    </Stack>
  );
}
